import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { fetchRecommendedTags } from "./api";
import { transformRecommendTagsResponse } from "./transformers";
import { filterTagRecommendQuery } from "@/utils/text-utils";
import type { UseRecommendTagsOptions } from "./types";

/**
 * Fetches recommended tags based on the content using React Query.
 * - Debounces requests to avoid firing on every keystroke
 * - Skips requests when content is below minLength
 * - Clears existing tags when content drops below minLength
 * - Cancels in-flight requests when content changes
 */
export const useRecommendTags = (
  content: string,
  options: UseRecommendTagsOptions = {}
) => {
  const { minLength = 20, threshold = 0.3, debounceMs = 1000 } = options;

  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce the content value using lodash
  const [debouncedContent, setDebouncedContent] = useState(content);

  const debouncedSetContent = useMemo(
    () => debounce((value: string) => setDebouncedContent(value), debounceMs),
    [debounceMs]
  );

  useEffect(() => {
    debouncedSetContent(content);
    return () => debouncedSetContent.cancel();
  }, [content, debouncedSetContent]);

  const enabled =
    Boolean(debouncedContent) && debouncedContent.length >= minLength;

  // Clear tags when content drops below minLength
  useEffect(() => {
    if (!enabled) {
      queryClient.removeQueries({
        queryKey: ["recommendTags"],
        exact: false,
      });
    }
  }, [enabled, queryClient]);

  const query = useQuery({
    queryKey: ["recommendTags", debouncedContent],
    queryFn: ({ signal }) => {
      // Cancel any previous in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Listen to React Query's signal to abort our controller
      signal?.addEventListener("abort", () => controller.abort());

      const filteredContent = filterTagRecommendQuery(debouncedContent);
      return fetchRecommendedTags(filteredContent, controller.signal);
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const tags = useMemo(() => {
    if (!enabled || !query.data) return [];
    return transformRecommendTagsResponse(query.data, threshold);
  }, [enabled, query.data, threshold]);

  const clearRecommendations = () => {
    queryClient.removeQueries({
      queryKey: ["recommendTags"],
      exact: false,
    });
  };

  return {
    tags,
    error: query.error,
    isLoading: query.isLoading && enabled,
    isError: query.isError,
    isSuccess: query.isSuccess,
    clearRecommendations,
  };
};
